import cn from 'classnames'
import { ReactNode, useCallback } from 'react'

import { MultiSegmentedControl } from 'cng-common/components/segmentedControl'
import { useFormField } from 'cng-common/features/form'
import { ValidationType } from 'cng-common/features/form/configs'
import { Translation } from 'cng-common/features/translations'

import styles from './MultiSegmentedFormField.module.scss'

interface IMultiSegmentedFormFieldProps {
  formKey: string
  field: string
  label: string
  options: readonly string[]
  defaultValue: string[]
  isRequired?: boolean
  isDisabled?: boolean
  /** Translation key shown when the field is required and nothing is selected. */
  requiredMessage?: string
  className?: string
  /** Optional helper text rendered between the label and the control. */
  hint?: ReactNode
  /** Optional content rendered below the control (e.g. a "Currently set to" summary). */
  footer?: ReactNode
}

/**
 * Bridges a multi-select `MultiSegmentedControl` (a presentational component) into the
 * cng-common Redux form. Stores the selection as a `string[]` under `field` and is reused
 * for both the Markets and Program Offerings fields.
 */
export const MultiSegmentedFormField = ({
  formKey,
  field,
  label,
  options,
  defaultValue,
  isRequired,
  isDisabled,
  requiredMessage,
  className,
  hint,
  footer,
}: IMultiSegmentedFormFieldProps) => {
  const onValidate = useCallback(
    (value: string[]) => (isRequired && !value?.length ? (requiredMessage ?? '') : ''),
    [isRequired, requiredMessage]
  )

  const { value, handleChange, validationMessage } = useFormField<string[]>({
    formKey,
    field,
    defaultValue,
    onValidate,
    isDisabled,
    validationType: ValidationType.ONCHANGE,
  })

  const handleSelect = useCallback(
    (keys: string[]) => {
      handleChange(keys)
    },
    [handleChange]
  )

  return (
    <div className={cn(styles.root, className)}>
      <div className={cn(styles.label, { [styles.required]: isRequired })}>
        <Translation text={label} span />
      </div>

      {hint && <div className={styles.hint}>{hint}</div>}

      <MultiSegmentedControl
        size="small"
        useItemKeys
        selectedItems={value ?? []}
        onMultiItemSelect={handleSelect}
        isDisabled={isDisabled}
        variant="light"
        rounded="rounded-full"
        backgroudn="bg-transparent"
        noWrap
      >
        {options.map((option) => (
          // Wrap in a <span> so the Item does not treat the string as a translation key.
          <MultiSegmentedControl.Item key={option} itemKey={option}>
            <span>{option}</span>
          </MultiSegmentedControl.Item>
        ))}
      </MultiSegmentedControl>

      {validationMessage && (
        <div className={styles.validationMessage}>
          <Translation text={validationMessage} span />
        </div>
      )}

      {footer && <div className={styles.footer}>{footer}</div>}
    </div>
  )
}

MultiSegmentedFormField.displayName = 'MultiSegmentedFormField'
